import React, { useState, useEffect } from "react";
import { supabase } from "../lib/supabaseClient";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronLeft,
  ChevronRight,
  Check,
  User,
  Activity,
  Ruler,
  Weight,
  Target,
  Zap,
  Phone,
} from "lucide-react";

const steps = [
  "Basic Info",
  "Preference",
  "Body",
  "Weight",
  "Target",
  "Goal",
  "Activity",
  "Health",
];

const CONDITIONS = [
  "None",
  "Diabetes",
  "PCOS",
  "Thyroid",
  "Hypertension",
  "Cholesterol",
  "Physical Injury",
  "Sleep issues",
];

const Onboarding = () => {
  const { user, fetchProfile } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState(0);
  const [loadingUser, setLoadingUser] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [form, setForm] = useState({
    name: "",
    phone: "",
    gender: "",
    food_preference: "",
    age: "",
    height: "",
    weight: "",
    target_weight: "",
    goal: "",
    activity_level: "",
    health_conditions: "",
  });

  /* -------------------------------
     AUTH LOAD HANDLING
  -------------------------------- */
  useEffect(() => {
    if (!user) {
      navigate("/login", { replace: true });
    } else {
      setLoadingUser(false);
    }
  }, [user, navigate]);

  /* -------------------------------
     HELPERS
  -------------------------------- */
  const update = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setError("");
  };

  const validateStep = () => {
    switch (step) {
      case 0:
        if (!form.name.trim()) return "Full name is required";
        if (!/^[6-9]\d{9}$/.test(form.phone))
          return "Enter a valid 10-digit phone number";
        if (!form.gender) return "Please select your gender";
        break;

      case 1:
        if (!form.food_preference) return "Select a food preference";
        break;

      case 2:
        if (!form.age || !form.height)
          return "Enter your age and height";
        break;

      case 3:
        if (!form.weight) return "Enter your current weight";
        break;

      case 4:
        if (!form.target_weight) return "Enter your target weight";
        break;

      case 5:
        if (!form.goal) return "Select a primary goal";
        break;

      case 6:
        if (!form.activity_level)
          return "Select your activity level";
        break;

      case 7:
        if (!form.health_conditions)
          return "Select a condition or 'None'";
        break;

      default:
        return null;
    }
    return null;
  };

  const next = () => {
    const err = validateStep();
    if (err) return setError(err);
    if (step < steps.length - 1) setStep((s) => s + 1);
  };

  const back = () => {
    if (step > 0) setStep((s) => s - 1);
  };

  /* -------------------------------
     SAVE PROFILE
  -------------------------------- */
  const saveProfile = async () => {
    const err = validateStep();
    if (err) return setError(err);
    if (saving) return;

    setSaving(true);

    const payload = {
      id: user.id,
      email: user.email,
      ...form,
      age: form.age ? Number(form.age) : null,
      height: form.height ? Number(form.height) : null,
      weight: form.weight ? Number(form.weight) : null,
      target_weight: form.target_weight
        ? Number(form.target_weight)
        : null,
      onboarding_complete: true,
    };

    const { error: dbError } = await supabase
      .from("profiles")
      .upsert(payload, { onConflict: "id" });

    if (dbError) {
      setSaving(false);
      return setError(dbError.message);
    }

    if (fetchProfile) {
      await fetchProfile(user.id);
    }

    navigate("/dashboard", { replace: true });
  };

  /* -------------------------------
     LOADER
  -------------------------------- */
  if (loadingUser) {
    return (
      <div className="h-screen flex items-center justify-center bg-white text-emerald-600 font-black animate-pulse uppercase tracking-widest">
        Initialising...
      </div>
    );
  }

  /* -------------------------------
     UI
  -------------------------------- */
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4 font-sans">
      <motion.div
        className="bg-white rounded-[40px] shadow-2xl max-w-md w-full p-8 relative overflow-hidden border border-gray-100"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        {/* Progress */}
        <div className="absolute top-0 left-0 right-0 h-1.5 bg-gray-50">
          <motion.div
            className="h-full bg-emerald-500"
            animate={{
              width: `${((step + 1) / steps.length) * 100}%`,
            }}
          />
        </div>

        <div className="mb-10 text-center">
          <Zap size={32} className="mx-auto mb-4 text-emerald-500" />
          <h2 className="text-2xl font-black text-gray-900">
            {steps[step]}
          </h2>
          <p className="text-gray-400 text-[10px] font-black uppercase mt-2 tracking-[0.2em]">
            Personalising your fitness plan
          </p>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, x: 10 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -10 }}
            className="min-h-[340px]"
          >
            {step === 0 && (
              <div className="space-y-4">
                <InputGroup
                  icon={<User size={20} />}
                  placeholder="Full Name"
                  value={form.name}
                  onChange={(v) => update("name", v)}
                />
                <InputGroup
                  icon={<Phone size={20} />}
                  placeholder="Phone Number"
                  value={form.phone}
                  onChange={(v) =>
                    update("phone", v.replace(/\D/g, ""))
                  }
                />

                <div className="grid grid-cols-2 gap-4 pt-2">
                  {["male", "female"].map((g) => (
                    <button
                      key={g}
                      onClick={() => update("gender", g)}
                      className={`p-6 rounded-[28px] border-2 font-black uppercase text-[10px] ${
                        form.gender === g
                          ? "border-emerald-500 bg-emerald-50 text-emerald-600"
                          : "border-gray-50 bg-gray-50 text-gray-300"
                      }`}
                    >
                      {g === "male" ? "🧔" : "👩"} {g}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {step === 1 && (
              <div className="grid gap-3">
                {[
                  { id: "veg", label: "Vegetarian", i: "🥦" },
                  { id: "non-veg", label: "Non-Vegetarian", i: "🍗" },
                  { id: "mixed", label: "Mixed Diet", i: "🍱" },
                ].map((p) => (
                  <OptionCard
                    key={p.id}
                    active={form.food_preference === p.id}
                    onClick={() =>
                      update("food_preference", p.id)
                    }
                    label={p.label}
                    icon={p.i}
                  />
                ))}
              </div>
            )}

            {step === 2 && (
              <div className="space-y-4">
                <InputGroup
                  icon={<Activity size={20} />}
                  placeholder="Age (years)"
                  value={form.age}
                  type="number"
                  onChange={(v) => update("age", v)}
                />
                <InputGroup
                  icon={<Ruler size={20} />}
                  placeholder="Height (cm)"
                  value={form.height}
                  type="number"
                  onChange={(v) => update("height", v)}
                />
              </div>
            )}

            {step === 3 && (
              <InputGroup
                icon={<Weight size={20} />}
                placeholder="Current Weight (kg)"
                value={form.weight}
                type="number"
                onChange={(v) => update("weight", v)}
              />
            )}

            {step === 4 && (
              <InputGroup
                icon={<Target size={20} />}
                placeholder="Target Weight (kg)"
                value={form.target_weight}
                type="number"
                onChange={(v) => update("target_weight", v)}
              />
            )}

            {step === 5 && (
              <div className="grid gap-3">
                {[
                  { id: "weight_loss", label: "Lose Weight", i: "📉" },
                  { id: "muscle_gain", label: "Gain Muscle", i: "💪" },
                  { id: "maintain", label: "Stay Fit", i: "🧘" },
                ].map((g) => (
                  <OptionCard
                    key={g.id}
                    active={form.goal === g.id}
                    onClick={() => update("goal", g.id)}
                    label={g.label}
                    icon={g.i}
                  />
                ))}
              </div>
            )}

            {step === 6 && (
              <div className="grid gap-2">
                {[
                  "sedentary",
                  "light",
                  "moderate",
                  "active",
                  "very_active",
                ].map((l) => (
                  <button
                    key={l}
                    onClick={() =>
                      update("activity_level", l)
                    }
                    className={`p-4 rounded-2xl border-2 font-bold uppercase text-xs ${
                      form.activity_level === l
                        ? "border-emerald-500 bg-emerald-50 text-emerald-600"
                        : "border-gray-50 bg-gray-50 text-gray-400"
                    }`}
                  >
                    {l.replace("_", " ")}
                  </button>
                ))}
              </div>
            )}

            {step === 7 && (
              <div className="grid grid-cols-2 gap-3">
                {CONDITIONS.map((c) => (
                  <button
                    key={c}
                    onClick={() =>
                      update("health_conditions", c)
                    }
                    className={`p-4 rounded-2xl border-2 font-bold text-[10px] uppercase ${
                      form.health_conditions === c
                        ? "border-emerald-500 bg-emerald-50 text-emerald-600"
                        : "border-gray-50 bg-gray-50 text-gray-400"
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        {error && (
          <p className="text-red-500 text-[10px] font-black uppercase text-center mt-4 bg-red-50 p-3 rounded-xl">
            {error}
          </p>
        )}

       <div className="flex items-center gap-4 mt-10">
  {/* Back Button */}
  <button
    onClick={back}
    aria-label="Go back"
    className={`group relative h-14 w-14 rounded-2xl flex items-center justify-center 
      bg-gray-50 border border-gray-200 text-gray-400 
      hover:bg-gray-100 hover:text-gray-600 transition-all
      active:scale-95
      ${step === 0 ? "opacity-0 pointer-events-none" : ""}
    `}
  >
    <ChevronLeft
      size={22}
      className="transition-transform group-hover:-translate-x-0.5"
    />
  </button>

  {/* Primary Action */}
  <button
    onClick={step === steps.length - 1 ? saveProfile : next}
    disabled={saving}
    className={`
      group relative flex-1 h-14 rounded-2xl 
      flex items-center justify-center gap-3
      font-black uppercase text-sm tracking-[0.2em]
      transition-all active:scale-[0.98]
      ${
        saving
          ? "bg-emerald-400 cursor-not-allowed"
          : "bg-emerald-600 hover:bg-emerald-700 shadow-lg shadow-emerald-500/20"
      }
      text-white
    `}
  >
    {/* Button Label */}
    <span className="relative z-10">
      {step === steps.length - 1
        ? saving
          ? "Saving..."
          : "Finish Setup"
        : "Continue"}
    </span>

    {/* Right Arrow */}
    {!saving && (
      <ChevronRight
        size={18}
        className="relative z-10 transition-transform group-hover:translate-x-0.5"
      />
    )}

    {/* Loading Overlay */}
    {saving && (
      <span className="absolute inset-0 flex items-center justify-center">
        <span className="h-5 w-5 border-2 border-white/40 border-t-white rounded-full animate-spin" />
      </span>
    )}
  </button>
</div>

      </motion.div>
    </div>
  );
};

/* -------------------------------
   COMPONENTS
-------------------------------- */
const OptionCard = ({ active, onClick, label, icon }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-4 p-5 rounded-[24px] border-2 ${
      active
        ? "border-emerald-500 bg-emerald-50"
        : "border-gray-50 bg-gray-50"
    }`}
  >
    <span className="text-2xl">{icon}</span>
    <span className="font-black uppercase text-[10px] tracking-widest">
      {label}
    </span>
    {active && (
      <Check size={18} className="ml-auto text-emerald-500" />
    )}
  </button>
);

const InputGroup = ({
  icon,
  placeholder,
  value,
  onChange,
  type = "text",
}) => (
  <div className="relative">
    <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-300">
      {icon}
    </div>
    <input
      type={type}
      className="bg-gray-50 p-5 pl-14 w-full rounded-[24px] font-black text-gray-700 focus:ring-2 focus:ring-emerald-500"
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    />
  </div>
);

export default Onboarding;
